// TODO(J-H): Consider renaming this store if we generalize further.
import api from "@/api";
import { pause } from "@/api/utils";
import WebSocketService from "@/services/webSocket";
import { useMessengerStore } from "@/stores/messenger";
import { useUserStore } from "@/stores/user";
import { useToast } from "vue-toastification";

// Used to override session id during prototype demos.
const EZEE_HASH_SESSION_ID = "ezee_session_id";
// SessionStorage key for session id.
const EZEE_STORAGE_SESSION_ID = "ezee.wfb.session_id";

const REQUIRED_PERM = "view-wfb-chat-page";
const DEFAULT_RECONNECT_INTERVAL = 300;
const DEFAULT_MAX_RECONNECT_ATTEMPT = 5000;

export const useWfbChatStore = defineStore("wfb-chat", {
  state: () => ({
    accessToken: null,
    // NOTE: refreshToken & orgToken are unused.
    refreshToken: null,
    orgToken: null,
    /** @type {WebSocketService|null} */
    websocket: null,
    /** @type {"MANUAL_DISCONNECT"|"CONNECTING"|"CONNECTED"|"DISCONNECTED"|"FAILED"} */
    connectionStatus: "MANUAL_DISCONNECT",
    history: new Map(),
    curReconnectAttempts: 0,
    isStreaming: false,
    isStoppingResponse: false,

    // Config stuff
    reconnectInterval: DEFAULT_RECONNECT_INTERVAL,
    maxReconnectAttempts: DEFAULT_MAX_RECONNECT_ATTEMPT,
  }),
  getters: {
    // I am pretty sure that the final WFB agent isn't prototype-style client
    // provided session id.
    sessionId() {
      const params = new URLSearchParams(window.location.search);
      let sessionId = params.get(EZEE_HASH_SESSION_ID);
      if (sessionId) {
        window.sessionStorage.setItem(EZEE_STORAGE_SESSION_ID, sessionId);
        console.log("saved session ID in URL params:", sessionId);
        return sessionId;
      }
      sessionId = window.sessionStorage.getItem(EZEE_STORAGE_SESSION_ID);
      console.log("session ID in SessionStorage:", sessionId);
      // Like I dont think "" should be allowed.
      if (!sessionId) {
        sessionId = window.crypto.randomUUID();
        window.sessionStorage.setItem(EZEE_STORAGE_SESSION_ID, sessionId);
        console.log("generated new session ID:", sessionId);
      }
      return sessionId;
    },
    lastMessage() {
      // Yeah, despite Map being ordered. Theres no way to get the last elem directly.
      // If this becomes a problem, add additional array to track order.
      return Array.from(this.history.values()).at(-1);
    },
  },
  actions: {
    /** Initialize WFB Chat Store and begin connecting to AC. */
    async initialize() {
      const userStore = useUserStore();
      if (!userStore.hasPerm(REQUIRED_PERM)) {
        await this.cleanup();
        return;
      }
      if (this.connectionStatus !== "MANUAL_DISCONNECT") await this.cleanup();
      this.connectionStatus = "DISCONNECTED";
      // Returns once websocket binded, so still in CONNECTING state.
      await this._reconnect();
    },
    /** Closes the AC connection and perform any other cleanup actions. */
    async cleanup() {
      if (this.websocket) this.websocket.close();
      this.$reset();
    },

    // Refer to frontend/src/views/messenger/components/MessageComposer.vue.
    async sendMessage(message, requestId, attachments = []) {
      // TODO: Replace this with unified attachment signing code.
      const { handleAttachmentsUpload } = useMessengerStore();

      // Use fake ID; history gets reset with real ID after refreshing anyways.
      const tempId = `temp-${window.crypto.randomUUID()}`;
      // Temporary message to display on frontend till next refresh since server
      // doesn't echo back user's message.
      this.history.set(tempId, {
        id: tempId,
        msg_type: "user",
        response: message,
        success: true,
        attachments: attachments.map((file) => ({
          filename: file.name,
          url: URL.createObjectURL(file),
        })),
      });

      const { transformedAttachments } =
        await handleAttachmentsUpload(attachments);

      const data = {
        version: "v1", // unused, but everything uses this value.
        session_id: this.sessionId,
        message, // must be non-empty, non-whitespace-only
        request_id: requestId,
        attachments: transformedAttachments,
      };
      this.websocket.send(JSON.stringify(data));
    },

    // Refer to frontend/src/views/messenger/components/MessageComposer.vue.
    async stopResponse(requestId) {
      const stopResponseURL = `${import.meta.env.VITE_APP_AC_SERVER_HTTPS_URL}/api/agentchat/stop-response/${requestId}?token=${this.accessToken}`;
      const stopResponseRes = await fetch(stopResponseURL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ session_id: this.sessionId }),
      });
      if (stopResponseRes.ok) {
        this.isStoppingResponse = true;
      };
      return stopResponseRes;
    },

    async _failWithMsg(msg) {
      this.connectionStatus = "FAILED";
      const toast = useToast();
      toast.error(msg);
    },

    async _fetchAuthTokens() {
      const userStore = useUserStore();
      // If error occurs, considered fatal and should bubble all the way up.
      ({
        data: {
          access_token: this.accessToken,
          refresh_token: this.refreshToken,
          org_token: this.orgToken,
        },
      } = await api.messenger.getAuthTokensForAcServer(userStore.activeOrganization));
    },

    /**
     * Only if in DISCONNECTED state, begin connecting. Returns once connection
     * process successfully starts. If disconnected then, websocket's event handler
     * triggers reconnection.
     */
    async _reconnect() {
      while (this.connectionStatus === "DISCONNECTED") {
        this.connectionStatus = "CONNECTING";
        if (this.websocket) this.websocket.close();

        try {
          await this._fetchAuthTokens();
        } catch (err) {
          console.error("error fetching auth tokens:", err);

          this.connectionStatus = "DISCONNECTED";
          await pause(this.reconnectInterval);
          continue;
        }
        // This is temporary cuz I need it to give up if there's no access token
        // anywhere. Long-term might make sense that setting this.accessToken
        // to null instead of throwing error can be used to signal failure.
        if (!this.accessToken) {
          console.error("no WFB access token available!");
          this._failWithMsg("Failed to authenticate, please refresh your page.")
          return;
        }

        const ws = api.wfb.getWfbWebSocketService(
          this.accessToken,
          this.sessionId
        );
        this._bindWebsocket(ws);
        ws.connect();
        this.websocket = ws;
      }
    },

    /** @param {WebSocketService} ws */
    _bindWebsocket(ws) {
      // Looking in WebSocketService, event val for connected/disconnected is useless.
      ws.on("connected", this._wsOnConnected);
      ws.on("disconnected", this._wsOnDisconnected);
      // This error is only emitted when the websocket is closed with non-1000 code,
      // so to prevent reconnection loop spam, we transition straight to failed.
      ws.on("error", this._wsOnError);
      ws.addListener(this._wsOnMessage);
    },

    _wsOnConnected() {
      console.log("connected");
      this.connectionStatus = "CONNECTED";
      this.curReconnectAttempts = 0;
    },

    _wsOnDisconnected() {
      console.log("disconnected while in state:", this.connectionStatus);

      if (this.connectionStatus === "MANUAL_DISCONNECT") return;
      else if (this.connectionStatus === "FAILED") return;
      else if (this.curReconnectAttempts >= this.maxReconnectAttempts) {
        this._failWithMsg("Unable to connect to Workflow Builder Agent Messenger.")
        return;
      }

      this.connectionStatus = "DISCONNECTED";
      this.curReconnectAttempts += 1;
      setTimeout(() => {
          console.log(
            `reconnect attempt: ${this.curReconnectAttempts}/${this.maxReconnectAttempts}`
          );

          this._reconnect();
        },
        this.reconnectInterval
      );
    },

    _wsOnError(evt) {
      console.error("server closed connection with fatal error", evt);
      // this._failWithMsg("Connection was closed unexpectedly, please refresh your page.")
      this.connectionStatus = "FAILED";
    },

    _wsOnMessage(data) {
      const hist = JSON.parse(data);
      // console.log("message:", hist);

      // Use $patch here to batch all updates efficiently.
      // Map used to maximize update speed since streaming, doesn't need assumption
      // of last message being the one streamed.
      this.$patch((state) => {
        let isAnyStreaming = false;
        let isMessageDone = false;
        hist.forEach((msg) => {
          // Throw if errors occur:
          if (msg.msg_type === "system" && msg.success === false) {
            console.error(msg);
            this._failWithMsg("The server encountered an error, please refresh your page.")
          }
          // Ack seems to use same id as previous message, thus overriding it?
          if (msg.msg_type === "system") return;

          // NOTE: This is definitely not complete, refer to below for more complete
          // implementation:
          // https://github.com/Proco-innovation-incorporated/wordpress-publicchat/blob/0a8f8f9dd31ddef127b577f5015aa123c894bb4c/src/App.vue#L226
          if (msg.extras?.message?.streaming) {
            if (!state.history.has(msg.id)) {
              state.history.set(msg.id, msg);
            } else {
              // Update existing message.
              state.history.get(msg.id).response += msg.response;
            }

            if (msg.extras?.message?.more) {
              isAnyStreaming = true;
            } else {
              // Final message, update everything else too.
              const finalResponse = state.history.get(msg.id).response;
              state.history.set(msg.id, { ...msg, response: finalResponse });
              isMessageDone = true;
            }
          } else {
            // When creating a new connection, server replays history.
            state.history.set(msg.id, msg);
          }
        });
        if (isMessageDone) {
          this.isStoppingResponse = false;
        };
        this.isStreaming = isAnyStreaming;
      });
    },
  },
});

/*
interface OutMsgDict {
  attachments: s3Attachment[]
  session_id: string
  message: string // non-empty
  version: "v1" // unused, but wordpress used this
}
*/

/*
{
  "id": "572fd679-5c05-47ed-b543-f6cdd92c4117",
  "response": "",
  "media_urls": [],
  "msg_type": "bot",
  "success": true,
  "attachments": [],
  "ws_source_id": null,
  "citations": [],
  "extras": {
    "message": {
      "group_id": "8ccd5632-1052-4467-a3c9-5cfe5795e0af",
      "more": false,
      "idx": null,
      "streaming": true
    }
  }
}
*/
